// Trade store: holds the current open trade session snapshot and pending requests.
import { create } from 'zustand';
import { Trade, type TradeSessionDTO } from '@/network/RestApi';
import { useUIStore } from './uiStore';
import { useCharacterStore } from './characterStore';

export interface IncomingTradeRequest {
  requestId: number;
  fromId: number;
  fromName: string;
}

interface TradeState {
  session: Extract<TradeSessionDTO, { id: number; status: number }> | null;
  incoming: IncomingTradeRequest[];
  loading: boolean;
  refresh: () => Promise<void>;
  pushIncoming: (r: IncomingTradeRequest) => void;
  removeIncoming: (requestId: number) => void;
  clearSession: () => void;
}

export const useTradeStore = create<TradeState>((set, get) => ({
  session: null,
  incoming: [],
  loading: false,
  refresh: async () => {
    const ch = useCharacterStore.getState().current;
    if (!ch) return;
    if (get().loading) return;
    set({ loading: true });
    try {
      const r = await Trade.current(ch.id);
      if ((r as { id: number }).id === 0) {
        set({ session: null });
      } else {
        set({ session: r as Extract<TradeSessionDTO, { id: number; status: number }> });
      }
    } catch {
      // ignore
    } finally {
      set({ loading: false });
    }
  },
  pushIncoming: (r) => set((s) => ({
    incoming: [r, ...s.incoming.filter((x) => x.requestId !== r.requestId)],
  })),
  removeIncoming: (requestId) =>
    set((s) => ({ incoming: s.incoming.filter((r) => r.requestId !== requestId) })),
  clearSession: () => set({ session: null }),
}));

// Helper used by notification listeners so they don't depend on UI store layout details.
export function openTradePanel() {
  useUIStore.getState().set('trade', true);
}
